import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {Chromosome, GenomeOption} from 'constants/GenomeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {CHROMOSOME_COORD_REGEX, parseChromosomeCoordinates} from 'molbio/crispr/BatchCRISPRUtil';
import GenomeRegionSelector from 'molbio/genome/GenomeRegionSelector';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_DARKER, GRAY_LIGHT, RED_DARK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Spinner from 'ui-library/Spinner';
import {chromosomeToName, cleanChromosomeName} from 'util/GenomeUtil';
import * as net from 'util/Net';

import {EnsemblInfoQuery} from './__generated/EnsemblInfoQuery';
import GenomeSearchSection from './GenomeSearchSection';
import {ChromosomalCoordInput, SequenceImportOptions} from './ImportSequenceConstants';
import SequenceImportOptionsForm from './SequenceImportOptionsForm';

interface AddChromosomalRegionsMenuProps {
  chromosomes: Array<Chromosome>;
  isChromosomal?: boolean;
  addSingleRow: (chr: Chromosome) => void;
  bulkAddRows: (rows: Array<ChromosomalCoordInput>) => void;
  toggleTestElement?: string;
}

function AddChromosomalRegionsMenu({
  chromosomes,
  isChromosomal,
  addSingleRow,
  bulkAddRows,
  toggleTestElement,
}: AddChromosomalRegionsMenuProps): JSX.Element {
  const regionNoun = isChromosomal === false ? 'region' : 'chromosome';
  const [sequenceText, setSequenceText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const nameToChromosome: {[name: string]: Chromosome} = {};
  for (const chr of chromosomes) {
    nameToChromosome[cleanChromosomeName(chr).toLowerCase()] = chr;
  }

  // TODO(BNCH-159939): the bulk-add textarea above can't currently be typed into.
  function handleBulkAdd(): void {
    const lines = sequenceText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length !== 0);
    const parsedCoords = parseChromosomeCoordinates(lines);
    if (parsedCoords == null) {
      const firstInvalidEntry = lines.find((line) => !CHROMOSOME_COORD_REGEX.test(line));
      setErrorMessage(`Entry "${firstInvalidEntry}" is not formatted correctly.`);
      return;
    }

    const chromosomeNameRegex = /^(?:chr)?(\w+(?=dna)|\w+)/i;
    const coords = [];
    for (const {name, start, end, cdsCoords} of parsedCoords) {
      const match = chromosomeNameRegex.exec(name);
      const chromosome = match != null ? nameToChromosome[match[1].toLowerCase()] : null;
      if (chromosome == null) {
        setErrorMessage(`${name} is an unrecognized ${regionNoun} name.`);
        return;
      }
      const cdsString = cdsCoords.map(({start, end}) => `${start}-${end}`).join(', ');
      coords.push({chromosome, start, end, cdsCoords: cdsString});
    }
    bulkAddRows(coords);
    setIsOpen(false);
    setSequenceText('');
    setErrorMessage('');
  }
  const toggleTestProps =
    toggleTestElement != null
      ? getTestProps({component: 'BatchChromosomeImportForm', element: toggleTestElement})
      : {};

  return (
    <Dropdown
      isOpen={isOpen}
      closeOnInsideClick={false}
      onToggle={setIsOpen}
      alwaysDropDown={true}
      menu={
        <Menu className={css(styles.addRegionsMenu)} width={MenuWidth.FULL} height={MenuHeight.MEDIUM}>
          <Option className={css(styles.selectRegionOption)}>
            Select region
            <Menu scrollable={true} className={css(styles.addRegionsSubmenu)}>
              {chromosomes.map((chr) => (
                <Option
                  key={chr.name}
                  onSelect={(chr: Chromosome) => {
                    setIsOpen(false);
                    addSingleRow(chr);
                  }}
                  value={chr}
                >
                  {chromosomeToName(chr)}
                </Option>
              ))}
            </Menu>
          </Option>
          <Option>
            <div>
              Quick add
              <div className={css(styles.quickAdd)}>
                <textarea
                  autoFocus={true}
                  value={sequenceText}
                  onChange={(e) => {
                    setSequenceText(e.target.value);
                  }}
                  placeholder={'chr2:3333-34465655\nchrX:334-40334555\nchr1:1-100 10-200,300-400'}
                  rows={3}
                  className={classNames(css(styles.quickAddTextArea), {
                    [css(styles.quickAddTextAreaError)]: errorMessage.length > 0,
                  })}
                />
                <Button
                  text="Add"
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.SMALL}
                  onClick={handleBulkAdd}
                />
              </div>
              {errorMessage.length > 0 && <p className={css(styles.quickAddHelpText)}>{errorMessage}</p>}
            </div>
          </Option>
        </Menu>
      }
    >
      <BenchlingIcon
        {...toggleTestProps}
        className={classNames(css(styles.addRegionsIcon), 'u-pointer')}
        onClick={() => {
          setIsOpen(true);
        }}
        icon="compose"
      />
    </Dropdown>
  );
}

export const defaultChromosomalCoordInput = {
  chromosome: null,
  start: null,
  end: null,
  cdsCoords: '',
};

export const ENSEMBL_INFO_QUERY = gql`
  query EnsemblInfoQuery @skipAudit @engComponent(name: MOLBIO) {
    ensemblInfo {
      currentVersion
    }
  }
`;

export interface BatchChromosomeImportFormProps {
  sequenceImportOptions: SequenceImportOptions;
  setSequenceImportOptions: (options: SequenceImportOptions) => void;
  chromosomalCoords: Array<ChromosomalCoordInput>;
  setChromosomalCoords: (regions: Array<ChromosomalCoordInput>) => void;
  genome?: GenomeOption;
  setGenome: (genome: GenomeOption | undefined) => void;
}

function BatchChromosomeImportForm({
  sequenceImportOptions,
  setSequenceImportOptions,
  chromosomalCoords,
  setChromosomalCoords,
  genome,
  setGenome,
}: BatchChromosomeImportFormProps): JSX.Element | null {
  const [isSearching, setIsSearching] = useState(false);
  const [chromosomes, setChromosomes] = useState<Array<Chromosome>>([]);
  const [isChromosomal, setIsChromosomal] = useState<boolean | undefined>(undefined);
  const [showNoneFoundWarning, setShowNoneFoundWarning] = useState(false);

  useEffect(() => {
    setChromosomes([]);
  }, [genome]);

  const queryResult = useBenchlingQuery<EnsemblInfoQuery>(ENSEMBL_INFO_QUERY);
  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load Ensembl information. Please try again.');
    return null;
  }

  const ensemblVersion = queryResult.data.ensemblInfo.currentVersion;

  async function fetchChromosomes(): Promise<void> {
    setChromosomalCoords([defaultChromosomalCoordInput]);
    setIsSearching(true);
    setShowNoneFoundWarning(false);
    setIsChromosomal(undefined);
    const regionsUrl =
      genome?.speciesName == null
        ? `/1/api/genomes/${genome!.id}/regions`
        : `/1/api/genomes/${genome.speciesName}/ensembl-regions`;
    const {results} = await net.getJSONPromise(regionsUrl);
    setChromosomes(results);
    setIsChromosomal(results[0]?.isChromosomal);
    if (results.length === 0) {
      setShowNoneFoundWarning(true);
    }
    setIsSearching(false);
  }

  function updateRegions(change: Partial<ChromosomalCoordInput>, index: number): void {
    const region = chromosomalCoords[index];
    const newRegion = {...region, ...change};
    setChromosomalCoords([
      ...chromosomalCoords.slice(0, index),
      newRegion,
      ...chromosomalCoords.slice(index + 1),
    ]);
  }

  function onChangeChromosome(chr: Chromosome, idx: number): void {
    analytics.track('sequence', 'select_files', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      chromosomalRegion: chr.name,
      genome: genome?.ensemblName,
      liveEnsemblUI: true,
    });
    updateRegions({chromosome: chr}, idx);
  }

  function removeRow(index: number): void {
    setChromosomalCoords(chromosomalCoords.filter((_, idx) => idx !== index));
  }

  function addRowWithChromosome(chromosome: Chromosome): void {
    setChromosomalCoords([...chromosomalCoords, {...defaultChromosomalCoordInput, chromosome}]);
  }

  function bulkAddRows(rows: Array<ChromosomalCoordInput>): void {
    setChromosomalCoords([...chromosomalCoords, ...rows]);
  }

  function onSelectGenome(newGenome: GenomeOption): void {
    analytics.track('sequence', 'change_genome', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      genome: newGenome.ensemblName,
      liveEnsemblUI: true,
    });
    setGenome(newGenome);
  }

  return (
    <div>
      {(chromosomes.length === 0 || isSearching) && (
        <p>We will import your data from the most recent Ensembl release version.</p>
      )}
      <GenomeSearchSection
        selectedGenome={genome}
        onSelectGenome={onSelectGenome}
        disableSearch={false}
        isSearching={isSearching}
        canClearSelection={false}
        onClear={() => {
          setGenome(undefined);
        }}
        onSearch={fetchChromosomes}
        helpText={
          <p className={css(styles.helpText)}>Gene, exon, and CDS annotations are included from Ensembl.</p>
        }
      />

      {isSearching && (
        <div className={css(styles.spinnerSection)}>
          <Spinner />
        </div>
      )}

      {showNoneFoundWarning && (
        <div className={css(styles.regionsSection)}>
          <InlineBanner bannerType={InlineBannerType.WARNING} text="No regions found" />
        </div>
      )}

      {chromosomes.length > 0 && !isSearching && (
        <>
          <div className={css(styles.regionsSection)}>
            {genome?.speciesName && ensemblVersion > 0 && (
              <p className={css(styles.helpText, styles.versionHelpText)}>
                {`We will import your data from the most recent Ensembl release version: ${ensemblVersion}.`}
              </p>
            )}
            <div className={css(styles.regionRow)}>
              <div className={css(styles.regionLabelContainer)}>
                <FieldLabel className={css(styles.regionLabel, styles.regionInputLabel)} label="Region(s)" />
                <AddChromosomalRegionsMenu
                  chromosomes={chromosomes}
                  isChromosomal={isChromosomal}
                  addSingleRow={addRowWithChromosome}
                  bulkAddRows={bulkAddRows}
                  toggleTestElement="addRegionsToggle"
                />
              </div>
              <FieldLabel className={css(styles.regionFreeInput, styles.regionInputLabel)} label="Start" />
              <FieldLabel className={css(styles.regionFreeInput, styles.regionInputLabel)} label="End" />
              <FieldLabel
                className={css(styles.regionFreeInput, styles.regionInputLabel)}
                label="CDS coordinates"
              />
            </div>

            {chromosomalCoords.map(({chromosome, start, end, cdsCoords}, idx) => (
              <div key={idx} className={css(styles.regionRow)}>
                <GenomeRegionSelector
                  testElement={`chromosomeSelector-${idx}`}
                  className={css(styles.chromosomeSelect)}
                  genome={null}
                  value={chromosome}
                  onChange={(chr) => {
                    onChangeChromosome(chr, idx);
                  }}
                  chromosomeRegions={chromosomes ?? []}
                />
                <TextInput
                  className={css(styles.regionFreeInput)}
                  value={start ?? ''}
                  onChange={(e) => {
                    const asNumber = parseInt(e.target.value, 10);
                    updateRegions({start: Number.isNaN(asNumber) ? null : asNumber}, idx);
                  }}
                  required={true}
                />
                <TextInput
                  className={css(styles.regionFreeInput)}
                  value={end ?? ''}
                  onChange={(e) => {
                    const asNumber = parseInt(e.target.value, 10);
                    updateRegions({end: Number.isNaN(asNumber) ? null : asNumber}, idx);
                  }}
                  required={true}
                />
                <TextInput
                  className={css(styles.regionFreeInput)}
                  value={cdsCoords}
                  onChange={(e) => {
                    updateRegions({cdsCoords: e.target.value}, idx);
                  }}
                />
                {chromosomalCoords.length > 1 && (
                  <div
                    onClick={() => {
                      removeRow(idx);
                    }}
                  >
                    <BenchlingIcon
                      className={classNames(css(styles.deleteIcon), 'u-pointer')}
                      icon="cross-thick"
                    />
                  </div>
                )}
              </div>
            ))}
            <p className={css(styles.helpText)}>
              {`The positions are 1-indexed, and the end is inclusive. The current size limit for sequences imported from ${
                genome?.speciesName != null ? 'Ensembl genomes is 5 Mb.' : 'private genomes is 10 Mb.'
              }`}
            </p>
          </div>

          <SequenceImportOptionsForm
            multipleSequences={chromosomalCoords.length > 1}
            sequenceImportOptions={sequenceImportOptions}
            folderIdOptions={{
              onChange: (folderId) => {
                setSequenceImportOptions({...sequenceImportOptions, folderId});
              },
            }}
            nucleotideTypeOptions={{
              isDisabled: true,
              disabledHelpText: 'Genomic sequences may only be imported as DNA.',
            }}
            tagSchemaIdOptions={{
              onChange: (tagSchemaId) => {
                setSequenceImportOptions({...sequenceImportOptions, tagSchemaId});
              },
            }}
            studySearchItemOptions={{
              onChange: (studySearchItems) => {
                setSequenceImportOptions({...sequenceImportOptions, studySearchItems});
              },
            }}
          />
        </>
      )}
    </div>
  );
}

const helpTextStyle = {
  color: GRAY_DARKER,
  marginTop: '6px',
};

const styles = StyleSheet.create({
  helpText: helpTextStyle,
  quickAddHelpText: {
    ...helpTextStyle,
    ...FONT_PROXIMA_11,
    marginBottom: '0',
  },
  versionHelpText: {
    ...helpTextStyle,
    marginTop: '16px',
  },
  spinnerSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    textAlign: 'center',
    paddingTop: '16px',
  },
  regionsSection: {
    paddingBottom: '16px',
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  regionRow: {
    display: 'flex',
    marginTop: '16px',
  },
  regionLabelContainer: {
    display: 'flex',
    marginRight: '16px',
    width: '230px',
  },
  regionLabel: {
    marginRight: '8px',
  },
  addRegionsIcon: {
    color: BLUE_LIGHT,
    marginTop: '3px',
  },
  chromosomeSelect: {
    marginRight: '16px',
    width: '230px',
  },
  regionFreeInput: {
    marginRight: '16px',
    width: '175px',
  },
  regionInputLabel: {
    color: GRAY_DARKER,
  },
  deleteIcon: {
    marginTop: '8px',
    color: GRAY,
  },
  addRegionsMenu: {
    width: '305px',
  },
  addRegionsSubmenu: {
    width: '220px',
  },
  selectRegionOption: {
    borderBottom: `1px solid ${GRAY}`,
  },
  quickAdd: {
    display: 'flex',
    marginTop: '6px',
  },
  quickAddTextArea: {
    width: '216px',
    marginRight: '6px',
  },
  quickAddTextAreaError: {
    border: `1px solid ${RED_DARK}`,
  },
});

export default BatchChromosomeImportForm;
